import React, { useEffect, useState } from 'react';
import { Download, ExternalLink, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { documentService } from '@/services/documentService';
import { DocumentRecord } from '@/types/database';
import { formatDate, formatFileSize } from '@/lib/utils';

interface DocumentPreviewModalProps {
  document: DocumentRecord | null;
  isOpen: boolean;
  onClose: () => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document: doc,
  isOpen,
  onClose,
}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && doc) {
      setLoading(true);
      setError(null);
      documentService
        .getSignedUrl(doc.file_path, 600) // 10 minutes signed URL
        .then((url) => {
          setSignedUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          console.error('[DocumentPreviewModal] Error loading signed URL:', err);
          setError(err.message || 'Failed to generate secure preview URL');
          setLoading(false);
        });
    } else {
      setSignedUrl(null);
    }
  }, [isOpen, doc]);

  if (!doc) return null;

  const isImage = doc.mime_type.startsWith('image/');
  const isPdf = doc.mime_type === 'application/pdf';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <div className="truncate">
              <DialogTitle className="truncate text-base sm:text-lg">{doc.file_name}</DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                {doc.document_type.replace(/_/g, ' ').toUpperCase()} • {formatFileSize(doc.file_size)} • Uploaded {formatDate(doc.created_at)}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Content Viewport */}
        <div className="flex-1 min-h-[300px] max-h-[65vh] bg-slate-950 rounded-lg overflow-hidden flex items-center justify-center relative my-2">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <p className="text-xs">Generating secure preview token...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 text-red-400 p-6 text-center">
              <AlertCircle className="w-8 h-8" />
              <p className="text-sm">{error}</p>
            </div>
          ) : isImage && signedUrl ? (
            <img src={signedUrl} alt={doc.file_name} className="max-w-full max-h-[62vh] object-contain" />
          ) : isPdf && signedUrl ? (
            <iframe src={signedUrl} title={doc.file_name} className="w-full h-[62vh] border-0" />
          ) : (
            <div className="flex flex-col items-center gap-3 text-slate-400 p-6 text-center">
              <FileText className="w-12 h-12" />
              <p className="text-sm">Preview not supported in-browser for this format.</p>
              {signedUrl && (
                <Button asChild size="sm" variant="default" className="bg-emerald-700 hover:bg-emerald-600">
                  <a href={signedUrl} download={doc.file_name} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-1.5" /> Download File
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        {doc.notes && (
          <div className="bg-slate-50 p-2.5 rounded-md border border-slate-200 text-xs text-slate-700">
            <strong>Notes:</strong> {doc.notes}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0 flex-row justify-between sm:justify-between items-center">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          {signedUrl && (
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline" className="text-xs">
                <a href={signedUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in New Tab
                </a>
              </Button>
              <Button asChild size="sm" variant="default" className="text-xs bg-emerald-700 hover:bg-emerald-600">
                <a href={signedUrl} download={doc.file_name}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </a>
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
